'use client';

import { Heading } from '../typography/Heading';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './Accordion';
import { Card } from './Card';
import { FlexContainer } from './FlexContainer';

const SECTION_VALUE = 'section';

export interface CollapsibleCardProps {
  /** Header label. A string renders as a section `Heading`; a node renders as-is. */
  title: React.ReactNode;
  /** Right-aligned header controls (e.g. a `RefreshButton`). Kept outside the toggle so they stay independently clickable. */
  headerActions?: React.ReactNode;
  /**
   * `bar` (default): static header bar; only a compact chevron button toggles — subtle, best when open by default.
   * `row`: the whole header row is the toggle, with a full-width hover highlight.
   */
  headerVariant?: 'bar' | 'row';
  /** Whether the card starts expanded. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}

/**
 * A `Card` whose body collapses under a header. The two `headerVariant`s share the
 * same card, open-state divider, and content spacing; they differ only in what toggles
 * and how hover reads.
 */
export function CollapsibleCard({
  title,
  headerActions,
  headerVariant = 'bar',
  defaultOpen = true,
  children,
}: CollapsibleCardProps): React.ReactElement {
  const heading = typeof title === 'string' ? <Heading size="sm">{title}</Heading> : title;
  const toggleLabel = typeof title === 'string' ? `Toggle ${title}` : 'Toggle section';

  return (
    <Card className="py-0">
      <Accordion collapsible defaultValue={defaultOpen ? SECTION_VALUE : undefined} type="single">
        <AccordionItem className="group" value={SECTION_VALUE} withBorder={false}>
          {headerVariant === 'row' ? (
            <div className="relative">
              {/* Rounded to match the card when collapsed; square bottom when open so it meets the content flush. */}
              <AccordionTrigger className="rounded-lg data-[state=open]:rounded-b-none data-[state=open]:border-b data-[state=open]:border-border-secondary">
                {heading}
              </AccordionTrigger>
              {headerActions ? (
                <div className="absolute right-11 top-1/2 -translate-y-1/2">{headerActions}</div>
              ) : null}
            </div>
          ) : (
            <FlexContainer
              className="border-b border-transparent p-4 group-data-[state=open]:border-border-secondary"
              items="center"
              justify="between"
            >
              {heading}
              <FlexContainer className="gap-2" items="center">
                {headerActions}
                <AccordionTrigger className="flex-none p-2" aria-label={toggleLabel} />
              </FlexContainer>
            </FlexContainer>
          )}
          <AccordionContent className="pt-4">{children}</AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}
